import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import supportIssuesApi from '../../utils/supportIssuesApi';

/**
 * @param {{
 *   issueExternalId: string;
 *   attachments: import('../../types/support').SupportAttachmentViewModel[];
 *   onAttachmentsChange: (attachments: import('../../types/support').SupportAttachmentViewModel[]) => void;
 * }} props
 */
export default function SupportAttachmentList({
  issueExternalId,
  attachments,
  onAttachmentsChange,
}) {
  const [loadingMap, setLoadingMap] = useState(/** @type {Record<string, 'download' | 'delete' | null>} */ ({}));
  const [errorMsg, setErrorMsg] = useState(/** @type {string | null} */ (null));

  function setLoading(id, action) {
    setLoadingMap((prev) => ({ ...prev, [id]: action }));
  }

  async function handleDownload(attachment) {
    if (!attachment.id) return;
    setLoading(attachment.id, 'download');
    setErrorMsg(null);
    try {
      const { blob, filename } = await supportIssuesApi.getAttachment(
        issueExternalId,
        attachment.id,
      );
      const name = filename || attachment.filename || `anexo-${attachment.id}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setErrorMsg('Erro ao baixar anexo.');
    } finally {
      setLoading(attachment.id, null);
    }
  }

  async function handleDelete(attachment) {
    if (!attachment.id) return;
    setLoading(attachment.id, 'delete');
    setErrorMsg(null);
    try {
      await supportIssuesApi.deleteAttachment(issueExternalId, attachment.id);
      onAttachmentsChange(attachments.filter((a) => a.id !== attachment.id));
    } catch {
      setErrorMsg('Erro ao excluir anexo.');
    } finally {
      setLoading(attachment.id, null);
    }
  }

  if (!attachments.length) {
    return (
      <Typography sx={{ fontSize: '0.82rem', color: '#aaa', mt: 0.5 }}>
        Nenhum anexo encontrado.
      </Typography>
    );
  }

  return (
    <Box>
      {errorMsg && (
        <Typography sx={{ fontSize: '0.8rem', color: '#d32f2f', mb: 0.5 }}>{errorMsg}</Typography>
      )}
      <List dense disablePadding>
        {attachments.map((att) => {
          const id = att.id || att.filename || '';
          const isDownloading = loadingMap[id] === 'download';
          const isDeleting = loadingMap[id] === 'delete';
          const isBusy = isDownloading || isDeleting;

          return (
            <ListItem
              key={id}
              disablePadding
              sx={{
                border: '1px solid #eee',
                borderRadius: '6px',
                mb: 0.5,
                px: 1,
                py: 0.25,
                background: '#fafafa',
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                  <Tooltip title="Baixar">
                    <span>
                      <IconButton
                        size="small"
                        disabled={isBusy}
                        onClick={() => handleDownload(att)}
                      >
                        {isDownloading ? (
                          <CircularProgress size={14} />
                        ) : (
                          <DownloadIcon sx={{ fontSize: '1rem', color: '#555' }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <span>
                      <IconButton
                        size="small"
                        disabled={isBusy}
                        onClick={() => handleDelete(att)}
                      >
                        {isDeleting ? (
                          <CircularProgress size={14} />
                        ) : (
                          <DeleteOutlineIcon sx={{ fontSize: '1rem', color: '#d32f2f' }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              }
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <AttachFileIcon sx={{ fontSize: '0.9rem', color: '#888' }} />
              </ListItemIcon>
              <ListItemText
                primary={att.filename || `Anexo ${id}`}
                primaryTypographyProps={{ fontSize: '0.82rem', color: '#333', noWrap: true }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
